import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import logger from '../utils/logger'

// Generate a session ID for funnel tracking
const generateSessionId = () => {
  return `wa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Store session ID for funnel progression tracking
let currentSessionId = generateSessionId()

export default function usePendoWhatsAppTracking() {
  const location = useLocation()

  const trackWhatsAppEvent = useCallback(
    (eventName: string, properties: Record<string, any> = {}) => {
      try {
        // Ensure we have the pendo object
        if (typeof window !== 'undefined' && window.pendo) {
          const fullEventName = eventName.startsWith('whatsapp_') ? eventName : `whatsapp_${eventName}`

          // Add common properties
          const enhancedProperties = {
            ...properties,
            timestamp: new Date().toISOString(),
            current_url: location.pathname,
            session_id: currentSessionId
          }

          logger.debug(`Tracking WhatsApp event: ${fullEventName}`, enhancedProperties)
          window.pendo.track(fullEventName, enhancedProperties)
        } else {
          logger.warn('Pendo not available for WhatsApp tracking:', eventName)
        }
      } catch (error) {
        logger.error('Error tracking WhatsApp event:', error, eventName, properties)
      }
    },
    [location.pathname]
  )

  // Generate new session ID for setup flow
  const startSetupSession = useCallback(() => {
    currentSessionId = generateSessionId()
    logger.debug('Started new WhatsApp setup session:', currentSessionId)
    return currentSessionId
  }, [])

  // Get current session ID
  const getSessionId = useCallback(() => currentSessionId, [])

  return {
    trackWhatsAppEvent,
    startSetupSession,
    getSessionId
  }
}

// Declare global pendo type for TypeScript
declare global {
  interface Window {
    pendo?: {
      track: (eventName: string, properties?: Record<string, any>) => void
      identify: (visitor: any, account?: any) => void
      initialize: (config: any) => void
    }
  }
}
